import React, { useState } from 'react';
import { FileText, Copy, Check, ChevronDown, ChevronUp, Lightbulb, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';

const SummaryDisplay = ({ summary, extractedText, examples, studyTips }) => {
  const [copied, setCopied] = useState(false);
  const [showFullText, setShowFullText] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success('Summary copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const wordCount = summary ? summary.split(/\s+/).length : 0;

  if (!summary) return null;

  return (
    <div className="space-y-6">
      {/* MAIN SUMMARY CARD */}
      <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-5 gap-2 sm:gap-0">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-brown-600" />
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Summary</h3>
            <span className="text-xs sm:text-sm text-brown-500">{wordCount} words</span>
          </div>
          <button onClick={handleCopy} className="flex items-center space-x-1.5 px-3 py-1.5 text-xs sm:text-sm text-brown-600 hover:bg-brown-50/50 rounded-lg transition-all font-medium">
            {copied ? <><Check className="w-4 h-4" /> <span>Copied!</span></> : <><Copy className="w-4 h-4" /> <span>Copy</span></>}
          </button>
        </div>

        <div className="prose max-w-none">
          <p className="text-sm sm:text-base md:text-lg text-brown-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
        </div>
      </div>

      {/* EXAMPLES SECTION */}
      {examples && examples.length > 0 && (
        <div className="backdrop-blur-sm bg-blue-50/30 rounded-xl p-4 sm:p-6 border border-blue-100 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-blue-900">Real-Life Examples</h3>
          </div>
          <div className="space-y-3">
            {examples.map((ex, i) => (
              <div key={i} className="bg-white/60 p-3 rounded-lg text-sm sm:text-base text-blue-800 border border-blue-50">
                {ex}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STUDY TIPS SECTION */}
      {studyTips && studyTips.length > 0 && (
        <div className="backdrop-blur-sm bg-amber-50/30 rounded-xl p-4 sm:p-6 border border-amber-100 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-amber-900">Study Tips & Memory Tricks</h3>
          </div>
          <ul className="list-disc list-inside space-y-2">
            {studyTips.map((tip, i) => (
              <li key={i} className="text-sm sm:text-base text-amber-800 leading-relaxed">{tip}</li>
            ))}
          </ul>
        </div>
      )}

      {/* EXTRACTED TEXT SECTION */}
      {extractedText && (
        <div className="mt-5 pt-5 border-t border-cream-300/50">
          <button onClick={() => setShowFullText(!showFullText)} className="flex items-center space-x-2 text-xs sm:text-sm text-brown-700 font-medium transition-colors">
            <FileText className="w-4 h-4" />
            <span>{showFullText ? 'Hide' : 'Show'} original textbook text</span>
            {showFullText ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showFullText && (
            <div className="mt-3 bg-cream-50/50 rounded-lg p-3 sm:p-4 border border-cream-200/50 animate-slide-up">
              <p className="text-xs sm:text-sm text-brown-700 leading-relaxed whitespace-pre-wrap font-mono">{extractedText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryDisplay;